import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { FileText, Loader2, Download, Trash2, Building2, CalendarRange, FileSpreadsheet, Filter, X, ChevronDown, History, ExternalLink } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AccuracyReport } from "@/components/AccuracyReport";
import { FxGainLossReport } from "@/components/FxGainLossReport";
import { XeroSyncReport } from "@/components/XeroSyncReport";
import { accountingApi } from "@/lib/apiClient";

const API = process.env.REACT_APP_BACKEND_URL;
const KINDS = [["weekly", "Weekly"], ["monthly", "Monthly"], ["yearly", "Fiscal year"]];

const Picker = ({ kind, setKind, anchor, setAnchor, periods, testPrefix }) => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="inline-flex rounded-lg border border-border p-0.5" data-testid={`${testPrefix}-kind`}>
      {KINDS.map(([k, l]) => (
        <button key={k} data-testid={`${testPrefix}-kind-${k}`} onClick={() => { setKind(k); setAnchor(periods?.[k]?.[0]?.anchor || ""); }}
                className={cn("rounded-md px-3 py-1.5 text-xs font-medium transition-colors", kind === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>{l}</button>
      ))}
    </div>
    <select data-testid={`${testPrefix}-anchor`} value={anchor} onChange={(e) => setAnchor(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[220px]">
      {(periods?.[kind] || []).map((p) => <option key={p.anchor} value={p.anchor}>{p.label}</option>)}
    </select>
  </div>
);

const STATUSES = [["approved", "Approved"], ["auto_coded", "Auto-coded"], ["pending_review", "Pending review"], ["rejected", "Rejected"]];

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
          className={cn("rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors", active ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground")}>{children}</button>
);

const FilterPanel = ({ f, setF, categories, reviewers, currencies }) => {
  const toggle = (key, v) => setF({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] });
  const count = f.gl_codes.length + f.statuses.length + f.reviewers.length + (f.currency ? 1 : 0);
  return (
    <div className="mt-4 rounded-lg border border-dashed border-border p-3" data-testid="report-filters">
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 font-medium"><Filter className="h-3.5 w-3.5 text-primary" /> Filters <span className="text-muted-foreground">(optional · {count ? `${count} active` : "none"})</span></span>
        {count > 0 && <button data-testid="report-filters-clear" onClick={() => setF({ gl_codes: [], statuses: [], reviewers: [], currency: "" })} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /> Clear</button>}
      </div>
      <div className="mt-2 grid gap-3 md:grid-cols-2">
        <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Status</div><div className="flex flex-wrap gap-1.5">{STATUSES.map(([k, l]) => <Chip key={k} testId={`filter-status-${k}`} active={f.statuses.includes(k)} onClick={() => toggle("statuses", k)}>{l}</Chip>)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Reviewer</div><div className="flex flex-wrap gap-1.5">{reviewers.map((r) => <Chip key={r} testId={`filter-reviewer-${r.replace(/\s+/g, "-")}`} active={f.reviewers.includes(r)} onClick={() => toggle("reviewers", r)}>{r}</Chip>)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">GL codes</div><div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">{categories.map((c) => <Chip key={c.code} testId={`filter-gl-${c.code}`} active={f.gl_codes.includes(c.code)} onClick={() => toggle("gl_codes", c.code)}>{c.code} · {c.name}</Chip>)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Currency</div><div className="flex flex-wrap gap-1.5">{currencies.map((c) => <Chip key={c} testId={`filter-currency-${c}`} active={f.currency === c} onClick={() => setF({ ...f, currency: f.currency === c ? "" : c })}>{c}</Chip>)}</div></div>
      </div>
    </div>
  );
};

const ReportRow = ({ r, onDelete }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-xs" data-testid={`report-row-${r.id}`}>
    <div className="min-w-0">
      <div className="font-medium truncate flex items-center gap-1.5">{r.supplier ? `${r.supplier} · ` : ""}{r.label} <span className="text-muted-foreground capitalize">· {r.kind}</span>
        <span data-testid={`report-format-${r.id}`} className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase", r.format === "xlsx" ? "bg-emerald-500/15 text-emerald-500" : "bg-primary/15 text-primary")}>{r.format === "xlsx" ? "Excel" : "PDF"}</span></div>
      <div className="text-muted-foreground">{new Date(r.created_at).toLocaleString()} · {r.created_by} · {r.summary?.invoices ?? 0} invoices · {(r.size / 1024).toFixed(0)} KB{r.filters?.description?.length ? <span data-testid={`report-filters-${r.id}`}> · {r.filters.description.join("; ")}</span> : null}</div>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {r.format !== "xlsx" && <a data-testid={`report-open-${r.id}`} href={`${API}/api/reports/${r.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Open</a>}
      <a data-testid={`report-download-${r.id}`} href={`${API}/api/reports/${r.id}/download?download=1`} className="inline-flex items-center gap-1 text-primary hover:underline"><Download className="h-3.5 w-3.5" /> {r.format === "xlsx" ? "Excel" : "Download"}</a>
      <button data-testid={`report-delete-${r.id}`} onClick={() => onDelete(r)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  </li>
);

export default function ReportsPage() {
  const { user, users } = useApp();
  const [periods, setPeriods] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [reports, setReports] = useState([]);
  const [fbConnected, setFbConnected] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [sageConnected, setSageConnected] = useState(false);
  useEffect(() => { accountingApi("freshbooks").status().then((r) => setFbConnected(!!r.connected || !!r.synced_count)).catch(() => {}); accountingApi("zoho").status().then((r) => setZohoConnected(!!r.connected || !!r.synced_count)).catch(() => {}); accountingApi("sage").status().then((r) => setSageConnected(!!r.connected || !!r.synced_count)).catch(() => {}); }, []);
  const { hash } = useLocation();
  useEffect(() => {
    const id = (hash || "").replace("#", "");
    if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    return () => clearTimeout(t);
  }, [hash, fbConnected, zohoConnected, sageConnected]);
  const [kind, setKind] = useState("monthly");
  const [anchor, setAnchor] = useState("");
  const [sKind, setSKind] = useState("monthly");
  const [sAnchor, setSAnchor] = useState("");
  const [supplier, setSupplier] = useState("");
  const [busy, setBusy] = useState(null);
  const [f, setF] = useState({ gl_codes: [], statuses: [], reviewers: [], currency: "" });
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const load = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => {
    api.get("/reports/periods").then((r) => { setPeriods(r.data); setAnchor(r.data.monthly[0].anchor); setSAnchor(r.data.monthly[0].anchor); });
    api.get("/suppliers").then((r) => { const list = r.data.items || r.data; setSuppliers(list); if (list[0]) setSupplier(list[0].supplier); });
    api.get("/categories").then((r) => setCategories((r.data || []).filter((c) => c.active !== false)));
    api.get("/fx/status").then((r) => setCurrencies(r.data.currencies || []));
    load();
  }, []);

  const generate = async (body, key) => {
    setBusy(key);
    try {
      const r = await api.post("/reports", { ...body, user });
      toast.success(`${r.data.label} ${body.format === "xlsx" ? "Excel workbook" : "PDF report"} ready — ${r.data.summary.invoices} invoice(s)${body.format === "xlsx" ? `, ${r.data.summary.lines} line items` : ""}`);
      window.open(`${API}/api/reports/${r.data.id}/download`, "_blank");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(null);
  };
  const del = async (r) => { await api.delete(`/reports/${r.id}`); toast.success("Report deleted"); load(); };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">Weekly, monthly and fiscal-year (April–March) reports as PDF or Excel for auditing, record-keeping and compliance.</p>
      </div>

      <section className="rounded-xl border border-border bg-card p-5" data-testid="workspace-report-card">
        <div className="flex items-center gap-2 mb-1"><CalendarRange className="h-4 w-4 text-primary" /><h2 className="text-base md:text-lg font-semibold">Workspace report</h2></div>
        <p className="text-xs text-muted-foreground mb-4">KPIs, spend by GL code, top suppliers, exceptions, who approved what, payments, FX and the full invoice register for the period.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Picker kind={kind} setKind={setKind} anchor={anchor} setAnchor={setAnchor} periods={periods} testPrefix="ws" />
          <Button data-testid="ws-generate" onClick={() => generate({ kind, anchor, format: "pdf", ...f, currency: f.currency || null }, "ws")} disabled={!anchor || !!busy}>
            {busy === "ws" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} Generate PDF
          </Button>
          <Button data-testid="ws-generate-xlsx" variant="outline" onClick={() => generate({ kind, anchor, format: "xlsx", ...f, currency: f.currency || null }, "ws-xlsx")} disabled={!anchor || !!busy}>
            {busy === "ws-xlsx" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-500" />} Export to Excel
          </Button>
        </div>
        <FilterPanel f={f} setF={setF} categories={categories} reviewers={users} currencies={currencies} />
      </section>

      <section className="rounded-xl border border-border bg-card p-5" data-testid="supplier-report-card">
        <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-primary" /><h2 className="text-base md:text-lg font-semibold">Supplier report</h2></div>
        <p className="text-xs text-muted-foreground mb-4">Every invoice and line item from one supplier in the period, plus performance: days to approve, on-time payment, GL codes, statements and open chases.</p>
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="supplier-select" value={supplier} onChange={(e) => setSupplier(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[220px]">
            {suppliers.map((s) => <option key={s.supplier_key || s.supplier} value={s.supplier}>{`${s.supplier} (${s.invoice_count})`}</option>)}
          </select>
          <Picker kind={sKind} setKind={setSKind} anchor={sAnchor} setAnchor={setSAnchor} periods={periods} testPrefix="sup" />
          <Button data-testid="sup-generate" onClick={() => generate({ kind: sKind, anchor: sAnchor, supplier, format: "pdf" }, "sup")} disabled={!supplier || !sAnchor || !!busy}>
            {busy === "sup" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} Generate PDF
          </Button>
          <Button data-testid="sup-generate-xlsx" variant="outline" onClick={() => generate({ kind: sKind, anchor: sAnchor, supplier, format: "xlsx" }, "sup-xlsx")} disabled={!supplier || !sAnchor || !!busy}>
            {busy === "sup-xlsx" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-500" />} Export to Excel
          </Button>
        </div>
      </section>

      <ReportHistory reports={reports} onDelete={del} />

      <AccuracyReport />
      <FxGainLossReport />

      <XeroSyncReport />
      {fbConnected && <XeroSyncReport provider="freshbooks" label="FreshBooks" />}
      {zohoConnected && <XeroSyncReport provider="zoho" label="Zoho Books" />}
      {sageConnected && <XeroSyncReport provider="sage" label="Sage Accounting" />}
    </div>
  );
}

const HISTORY_FILTERS = [["all", "All reports"], ["workspace", "Workspace reports"], ["supplier", "Supplier reports"], ["pdf", "PDF only"], ["xlsx", "Excel only"]];

const ReportHistory = ({ reports, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const list = reports.filter((r) => {
    if (filter === "workspace") return !r.supplier;
    if (filter === "supplier") return !!r.supplier;
    if (filter === "pdf") return r.format !== "xlsx";
    if (filter === "xlsx") return r.format === "xlsx";
    return true;
  });
  return (
    <section className="rounded-xl border border-border bg-card" data-testid="report-history">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold hover:text-primary" data-testid="report-history-toggle">
            <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
            <History className="h-4 w-4 text-muted-foreground" /> Report history <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">{reports.length}</span>
          </CollapsibleTrigger>
          {open && (
            <select data-testid="report-history-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs">
              {HISTORY_FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
          {!open && reports[0] && <span className="text-xs text-muted-foreground" data-testid="report-history-latest">Latest: {reports[0].supplier ? `${reports[0].supplier} · ` : ""}{reports[0].label} · {new Date(reports[0].created_at).toLocaleDateString()}</span>}
        </div>
        <CollapsibleContent className="border-t border-border px-5 py-4">
          {reports.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="report-history-empty">No reports yet — generate one above.</p>
            : list.length === 0 ? <p className="text-sm text-muted-foreground">No reports of this type yet.</p>
            : <ul className="space-y-2 max-h-[420px] overflow-y-auto d20-scroll pr-1">{list.map((r) => <ReportRow key={r.id} r={r} onDelete={onDelete} />)}</ul>}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
};
